/* eslint-disable indent */
import { IMedia } from "../../../model/unityObject";
import { ContentType, isSceneContentType } from "./dataTypes/ContentType";
import { ContentFieldSchemaEntry, FieldDescriptor, FieldWidget } from "./FieldDescriptor";

export type FieldType = Field["type"];
export type MediaType = MediaField["mediaType"];

export type Field = ScalarField | MediaField | LongTextField | ArrayOfObjectsField;
export type ScalarField = TextField | BooleanField;

type TextField = {
  type: "text";
  fieldName: string;
  currentValue?: string;
  descriptor?: FieldDescriptor;
};
type LongTextField = {
  type: "longtext";
  fieldName: string;
  currentValue?: string;
  descriptor?: FieldDescriptor;
};
export type ArrayOfObjectsField = {
  type: "arrayOfObjects";
  fieldName: string;
  currentValue?: any[];
  descriptor?: FieldDescriptor;
};
export type MediaField = {
  type: "media";
  mediaType: "video" | "image" | "audio" | "pdf" | "model3d" | "avatar";
  fieldName: string;
  currentValue?: number | IMedia;
  descriptor?: FieldDescriptor;
};
type BooleanField = {
  type: "boolean";
  fieldName: string;
  currentValue?: boolean;
  descriptor?: FieldDescriptor;
};

export const isScalarField = (field: Field): field is ScalarField =>
  field.type === "text" || field.type === "boolean";
export const isMediaField = (field: Field): field is MediaField => field.type === "media";

const inferMediaTypeFromKey = (key: string): MediaField["mediaType"] => {
  if (key.includes("audio")) return "audio";
  if (key.includes("model3d")) return "model3d";
  if (key.includes("pdf")) return "pdf";
  if (key.includes("video")) return "video";
  if (key.includes("avatar")) return "avatar";
  return "image";
};

const mapWidgetToFieldType = (widget: FieldWidget): Field["type"] => {
  switch (widget) {
    case "text":
      return "text";
    case "longtext":
      return "longtext";
    case "boolean":
      return "boolean";
    case "arrayOfObjects":
      return "arrayOfObjects";
    case "media":
      return "media";
  }
};

const readCurrentValue = (
  descriptor: FieldDescriptor,
  contentType: ContentType,
  relevantPld: any,
): any => {
  if (descriptor.pldKey && relevantPld && relevantPld[descriptor.pldKey] !== undefined) {
    return relevantPld[descriptor.pldKey];
  }
  return (contentType.object as any)?.[descriptor.key];
};

export const extractFieldsFromSchema = (
  contentType: ContentType,
  schema: ContentFieldSchemaEntry,
  relevantPld: any,
): Field[] => {
  return schema.fields.map((descriptor): Field => {
    const currentValue = readCurrentValue(descriptor, contentType, relevantPld);
    const type = mapWidgetToFieldType(descriptor.widget);
    if (type === "media") {
      return {
        type: "media",
        mediaType: inferMediaTypeFromKey(descriptor.key),
        fieldName: descriptor.key,
        currentValue,
        descriptor,
      };
    }
    if (type === "boolean") {
      return {
        type: "boolean",
        fieldName: descriptor.key,
        currentValue: !!currentValue,
        descriptor,
      };
    }
    if (type === "longtext") {
      return {
        type: "longtext",
        fieldName: descriptor.key,
        currentValue,
        descriptor,
      };
    }
    if (type === "arrayOfObjects") {
      return {
        type: "arrayOfObjects",
        fieldName: descriptor.key,
        currentValue: Array.isArray(currentValue) ? currentValue : [],
        descriptor,
      };
    }
    return {
      type: "text",
      fieldName: descriptor.key,
      currentValue,
      descriptor,
    };
  });
};

export const extractFieldsFromContentType = (contentType: ContentType): Field[] => {
  // NO VALIDATION FOR THE FOLLOWING ELEMENTS
  if (contentType.modelName === "content-scoring-scene") {
    return [];
  }

  // the result array
  // there is always the name field to validate.
  // 24-char descriptor matches the in-VR creator cap (mirrors what
  // buildUnifiedSchema.js sets for slopify-supported types).
  const fields: Field[] = [
    {
      type: "text",
      fieldName: "name",
      currentValue: contentType.object.name,
      descriptor: {
        key: "name",
        widget: "text",
        jsonSchema: { type: "string", maxLength: 24 },
        description: null,
        required: true,
        pldKey: null,
        isTranslatable: false,
      },
    },
  ];

  if (isSceneContentType(contentType)) {
    fields.push({
      type: "media",
      mediaType: "image",
      fieldName: "source_media",
      currentValue: contentType.object.source_media,
    });
    fields.push({
      type: "media",
      mediaType: "model3d",
      fieldName: "source_model3d",
      currentValue: contentType.object.source_model3d,
    });
  } else {
    switch (contentType.modelName) {
      case "content-audio":
        fields.push({
          type: "media",
          mediaType: "audio",
          fieldName: "source_audio",
          currentValue: contentType.object.source_audio,
        });
        break;
      case "content-onboarding":
        fields.push({
          type: "media",
          mediaType: "image",
          fieldName: "source_image_2d",
          currentValue: contentType.object.source_image_2d,
        });
        break;
      case "content-thumbnail":
        fields.push({
          type: "media",
          mediaType: "image",
          fieldName: "source_image",
          currentValue: contentType.object.source_image,
        });
        break;
      case "content-quizz-2":
        fields.push(
          {
            type: "media",
            mediaType: "image",
            fieldName: "answer_1_image",
            currentValue: contentType.object.answer_1_image,
          },
          {
            type: "media",
            mediaType: "image",
            fieldName: "answer_2_image",
            currentValue: contentType.object.answer_2_image,
          },
          {
            type: "media",
            mediaType: "image",
            fieldName: "answer_3_image",
            currentValue: contentType.object.answer_3_image,
          },
        );
        break;
      case "content-tuto":
        fields.push({
          type: "media",
          mediaType: "image",
          fieldName: "source_logo",
          currentValue: contentType.object.source_logo,
        });
        break;
      case "content-video-2-d":
        fields.push({
          type: "media",
          mediaType: "video",
          fieldName: "source_media",
          currentValue: contentType.object.source_media,
        });
        break;
      case "content-pdf-viewer":
        fields.push({
          type: "media",
          mediaType: "pdf",
          fieldName: "source_pdf",
          currentValue: contentType.object.source_pdf,
        });
        break;
      case "content-3d-viewer":
      case "content-3-d-viewer":
        fields.push({
          type: "media",
          mediaType: "model3d",
          fieldName: "source_model3d",
          currentValue: contentType.object.source_model3d,
        });
        break;
      case "content-avatar-audio-player":
        fields.push({
          type: "media",
          mediaType: "audio",
          fieldName: "source_audio",
          currentValue: contentType.object.source_audio,
        });
        fields.push({
          type: "media",
          mediaType: "avatar",
          fieldName: "source_avatar",
          currentValue: contentType.object.source_avatar,
        });
        break;
      case "content-speech-to-check":
      case "content-photo-catch":
      case "content-quizz-1":
      case "content-hotspot-scene-image":
      case "content-hotspot-scene-video":
      case "content-text":
      case "content-zone-detect":
      case "content-quizz-3":
      case "content-yes-no-question":
      case "content-zone-sort":
      case "content-element-catch":
      case "content-highlight":
      case "content-danger-catch":
      case "content-tuto2":
      case "content-quizz-sort":
      case "content-fire-extinguisher":
      case "content-fire":
      case "content-dust":
      case "content-quizz-image-multiple":
      case "content-avatar-ai":
      case "content-avatar-ai-persona":
        break;
      /*
      case "content-button-1":
      case "content-button-2":
      case "content-clean":
      case "content-scoring-general":
      case "content-vr-locator":
      case "content-zone-detect":
        break; // only the name field is of interest
       */
    }
  }

  return fields;
};
